import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Database, ChevronRight, ChevronDown, ExternalLink, Loader2, Search, Upload, Download } from 'lucide-react';
import {
  fetchBkStatus, bkCollect, bkImport, fetchBkKeywords,
  fetchBkYears, fetchBkMonths, fetchBkDays, fetchBkArticles,
  fetchBkJob, bkExportUrl, bkCollectNaver, fetchBkPartnerSummary,
} from '../api';

function ImportForm({ onDone }) {
  const [kw, setKw] = useState('');
  const [file, setFile] = useState(null);
  const [msg, setMsg] = useState('');
  const [dragging, setDragging] = useState(false);

  const mut = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('keyword', kw || file.name.replace(/\.[^.]+$/, ''));
      return bkImport(fd);
    },
    onSuccess: (d) => {
      setMsg(`✅ ${d.total?.toLocaleString()}행 중 ${d.inserted?.toLocaleString()}건 저장 완료 (키워드: ${d.keyword})`);
      setFile(null);
      onDone();
    },
    onError: (e) => setMsg(`❌ ${e.response?.data?.error || e.message}`),
  });

  function handleDrop(e) {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f && (f.name.endsWith('.xlsx') || f.name.endsWith('.xls'))) setFile(f);
    else setMsg('❌ xlsx 또는 xls 파일만 가능합니다.');
  }

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <Upload size={15} color="#4ade80" /> 빅카인즈 엑셀 파일 임포트
      </h2>
      <div style={{ fontSize: '0.75rem', color: '#64748b', marginBottom: '0.75rem' }}>
        빅카인즈 웹(bigkinds.or.kr) → 뉴스검색·분석 → 엑셀 다운로드 후 여기에 업로드하세요. (무료, 최대 20,000건)
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.75rem' }}>
        <div>
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '3px' }}>검색 키워드 (태그용, 비워두면 파일명 사용)</div>
          <input value={kw} onChange={e => setKw(e.target.value)} placeholder="예: 원전, 한수원"
            style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#e2e8f0', padding: '5px 10px', fontSize: '0.85rem', width: '180px' }} />
        </div>
      </div>

      <div
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => document.getElementById('bk-file-input').click()}
        style={{
          border: `2px dashed ${dragging ? '#38bdf8' : '#334155'}`,
          borderRadius: '8px', padding: '1.5rem', textAlign: 'center',
          cursor: 'pointer', background: dragging ? '#0c2a3a' : '#0f172a',
          transition: 'all 0.15s', marginBottom: '0.75rem',
        }}>
        <input id="bk-file-input" type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
          onChange={e => { if (e.target.files[0]) setFile(e.target.files[0]); }} />
        <Upload size={24} color="#475569" style={{ marginBottom: '0.4rem' }} />
        <div style={{ fontSize: '0.83rem', color: file ? '#4ade80' : '#64748b' }}>
          {file ? `📄 ${file.name} (${(file.size / 1024).toFixed(0)} KB)` : '클릭하거나 xlsx 파일을 드래그하세요'}
        </div>
      </div>

      <button onClick={() => { setMsg(''); mut.mutate(); }} disabled={!file || mut.isPending}
        style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: !file || mut.isPending ? '#1e293b' : '#065f46', border: 'none', borderRadius: '6px', color: !file ? '#475569' : '#fff', padding: '6px 14px', fontSize: '0.85rem', cursor: !file || mut.isPending ? 'default' : 'pointer' }}>
        {mut.isPending ? <><Loader2 size={13} style={{ animation: 'spin 0.7s linear infinite' }} /> 처리 중...</> : <><Upload size={13} /> 임포트</>}
      </button>

      {msg && <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: msg.startsWith('✅') ? '#4ade80' : '#f87171' }}>{msg}</div>}
    </div>
  );
}

function CollectForm({ onDone }) {
  const [kw, setKw] = useState('원전');
  const [from, setFrom] = useState('2005-01-01');
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [msg, setMsg] = useState('');
  const [jobId, setJobId] = useState(null);
  const [source, setSource] = useState('naver');

  const mut = useMutation({
    mutationFn: () => (source === 'naver' ? bkCollectNaver : bkCollect)({ keyword: kw, date_from: from, date_to: to }),
    onSuccess: (d) => {
      setJobId(d.job_id);
      setMsg(d.already_running ? '⏳ 동일 키워드 수집 작업이 이미 진행 중입니다. 진행률을 표시합니다.' : '');
    },
    onError: (e) => setMsg(`❌ ${e.response?.data?.error || e.message}`),
  });

  const { data: job } = useQuery({
    queryKey: ['bk-job', jobId],
    queryFn: () => fetchBkJob(jobId),
    enabled: !!jobId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return (s === 'completed' || s === 'failed' || s === 'partial') ? false : 2000;
    },
  });

  React.useEffect(() => {
    if (!job) return;
    if (job.status === 'completed') {
      setMsg(`✅ 완료: 전체 ${job.total_hits?.toLocaleString()}건 중 ${job.fetched?.toLocaleString()}건 수집, ${job.inserted?.toLocaleString()}건 신규 저장`);
      setJobId(null);
      onDone();
    } else if (job.status === 'partial') {
      setMsg(`⚠️ 부분 완료: ${job.fetched?.toLocaleString()}건 수집 (${job.failed_pages}개 페이지 실패). 재실행하면 누락분이 보완됩니다.`);
      setJobId(null);
      onDone();
    } else if (job.status === 'failed') {
      setMsg(`❌ 수집 실패: ${job.error_message || '알 수 없는 오류'}`);
      setJobId(null);
    }
  }, [job]);

  const running = !!jobId && job && (job.status === 'pending' || job.status === 'running');
  const pct = job && job.total_hits ? Math.min(100, Math.round((job.fetched / Math.max(job.total_hits, job.fetched)) * 100)) : 0;

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#f1f5f9', marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <Search size={15} /> 기사 장기 수집
      </h2>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '3px' }}>수집 소스</div>
          <div style={{ display: 'flex', gap: '0.3rem' }}>
            {[['naver', '네이버 (전문지 포함)'], ['bigkinds', '빅카인즈 (주요지)']].map(([v, label]) => (
              <button key={v} type="button" onClick={() => setSource(v)}
                style={{ padding: '5px 10px', borderRadius: '6px', border: '1px solid #334155', fontSize: '0.78rem', background: source === v ? '#1d4ed8' : '#0f172a', color: source === v ? '#fff' : '#94a3b8', cursor: 'pointer' }}>
                {label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '3px' }}>검색 키워드</div>
          <input value={kw} onChange={e => setKw(e.target.value)}
            style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#e2e8f0', padding: '5px 10px', fontSize: '0.85rem', width: '140px' }} />
        </div>
        <div>
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '3px' }}>시작일</div>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#e2e8f0', padding: '5px 10px', fontSize: '0.85rem' }} />
        </div>
        <div>
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '3px' }}>종료일</div>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#e2e8f0', padding: '5px 10px', fontSize: '0.85rem' }} />
        </div>
        <button onClick={() => { setMsg(''); mut.mutate(); }} disabled={mut.isPending || running || !kw}
          style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: (mut.isPending || running) ? '#1e293b' : '#1d4ed8', border: 'none', borderRadius: '6px', color: '#fff', padding: '6px 14px', fontSize: '0.85rem', cursor: (mut.isPending || running) ? 'default' : 'pointer' }}>
          {running ? <><Loader2 size={13} style={{ animation: 'spin 0.7s linear infinite' }} /> 수집 중...</> : '수집 시작'}
        </button>
      </div>
      {running && job && (
        <div style={{ marginTop: '0.6rem' }}>
          <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '0.3rem' }}>
            ⏳ 수집 중: {job.fetched?.toLocaleString()} / {job.total_hits ? job.total_hits.toLocaleString() + '건' : '집계 중'}
            {job.chunks_total > 1 && ` · 구간 ${job.chunks_done}/${job.chunks_total}`}
            {job.current_range && ` · 현재 ${job.current_range}`}
            {job.failed_pages > 0 && <span style={{ color: '#fbbf24' }}> · 실패 페이지 {job.failed_pages}</span>}
          </div>
          <div style={{ background: '#1e293b', borderRadius: '4px', height: '6px', overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: '#3b82f6', transition: 'width 0.5s' }} />
          </div>
        </div>
      )}
      {msg && <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: msg.startsWith('✅') ? '#4ade80' : '#f87171' }}>{msg}</div>}
    </div>
  );
}

function ArticleList({ params }) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ['bk-articles', params, page],
    queryFn: () => fetchBkArticles({ ...params, page, limit: 50 }),
    enabled: !!(params.date || params.month || params.year),
    keepPreviousData: true,
  });

  if (isLoading) return <div style={{ padding: '1rem', color: '#64748b', fontSize: '0.8rem' }}><Loader2 size={14} style={{ animation: 'spin 0.7s linear infinite' }} /> 로딩 중...</div>;
  if (!data || data.articles.length === 0) return <div style={{ padding: '0.5rem', color: '#475569', fontSize: '0.8rem' }}>기사가 없습니다.</div>;

  return (
    <div>
      <div style={{ fontSize: '0.72rem', color: '#475569', marginBottom: '0.5rem' }}>총 {data.total?.toLocaleString()}건</div>
      {data.articles.map((a, i) => (
        <div key={a.id} style={{ padding: '0.4rem 0', borderBottom: '1px solid #1e293b', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
          <span style={{ color: '#475569', fontSize: '0.72rem', minWidth: '1.6rem', flexShrink: 0, paddingTop: '2px' }}>{(page - 1) * 50 + i + 1}.</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.83rem', color: '#e2e8f0', lineHeight: 1.45 }}>
              {a.link ? (
                <a href={a.link} target="_blank" rel="noopener noreferrer"
                  style={{ color: '#e2e8f0', display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
                  {a.title} <ExternalLink size={10} opacity={0.4} />
                </a>
              ) : a.title}
            </div>
            <div style={{ fontSize: '0.68rem', color: '#475569', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
              {a.source} · {a.published_at}
              {a.partner_hint === 'cp' && <span style={{ fontSize: '0.6rem', padding: '0.05rem 0.3rem', borderRadius: '3px', background: '#164e63', color: '#67e8f9' }}>CP</span>}
              {a.partner_hint === 'search' && <span style={{ fontSize: '0.6rem', padding: '0.05rem 0.3rem', borderRadius: '3px', background: '#1e293b', color: '#94a3b8' }}>검색제휴</span>}
            </div>
            {a.summary && <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: '3px', lineHeight: 1.4 }}>{a.summary.slice(0, 120)}{a.summary.length > 120 ? '…' : ''}</div>}
          </div>
        </div>
      ))}
      {data.pages > 1 && (
        <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.75rem', justifyContent: 'center' }}>
          {Array.from({ length: Math.min(data.pages, 10) }, (_, i) => i + 1).map(p => (
            <button key={p} onClick={() => setPage(p)} style={{
              padding: '3px 9px', borderRadius: '5px', border: '1px solid #334155', fontSize: '0.75rem',
              background: p === page ? '#1d4ed8' : '#0f172a', color: p === page ? '#fff' : '#94a3b8', cursor: 'pointer',
            }}>{p}</button>
          ))}
        </div>
      )}
    </div>
  );
}

function TreeNode({ keyword, year }) {
  const [openMonth, setOpenMonth] = useState(null);
  const [openDay, setOpenDay] = useState(null);
  const [selectedParams, setSelectedParams] = useState(null);

  const { data: months = [] } = useQuery({
    queryKey: ['bk-months', keyword, year],
    queryFn: () => fetchBkMonths(keyword, year),
  });

  return (
    <div style={{ marginLeft: '1rem' }}>
      {months.map(m => (
        <div key={m.month}>
          <div onClick={() => { setOpenMonth(openMonth === m.month ? null : m.month); setOpenDay(null); setSelectedParams({ keyword, month: m.month }); }}
            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '3px 0', cursor: 'pointer', color: openMonth === m.month ? '#38bdf8' : '#94a3b8', fontSize: '0.82rem' }}>
            {openMonth === m.month ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            {m.month} <span style={{ color: '#475569', fontSize: '0.7rem' }}>({m.count?.toLocaleString()}건)</span>
          </div>

          {openMonth === m.month && (
            <DayTree keyword={keyword} month={m.month} openDay={openDay} setOpenDay={setOpenDay} setSelectedParams={setSelectedParams} />
          )}
        </div>
      ))}

      {selectedParams && (
        <div style={{ marginTop: '0.75rem', borderTop: '1px solid #1e293b', paddingTop: '0.75rem' }}>
          <ArticleList params={selectedParams} />
        </div>
      )}
    </div>
  );
}

function DayTree({ keyword, month, openDay, setOpenDay, setSelectedParams }) {
  const { data: days = [] } = useQuery({
    queryKey: ['bk-days', keyword, month],
    queryFn: () => fetchBkDays(keyword, month),
  });

  return (
    <div style={{ marginLeft: '1rem' }}>
      {days.map(d => (
        <div key={d.day} onClick={() => { setOpenDay(d.day); setSelectedParams({ keyword, date: d.day }); }}
          style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '2px 0', cursor: 'pointer', color: openDay === d.day ? '#a78bfa' : '#64748b', fontSize: '0.78rem' }}>
          <ChevronRight size={11} />
          {d.day} <span style={{ color: '#334155', fontSize: '0.68rem' }}>({d.count}건)</span>
        </div>
      ))}
    </div>
  );
}

function BrowsePanel() {
  const [selKeyword, setSelKeyword] = useState('');
  const [openYear, setOpenYear] = useState(null);
  const [showPartners, setShowPartners] = useState(false);

  const { data: keywords = [] } = useQuery({ queryKey: ['bk-keywords'], queryFn: fetchBkKeywords });
  const { data: years = [] } = useQuery({
    queryKey: ['bk-years', selKeyword],
    queryFn: () => fetchBkYears(selKeyword || undefined),
  });
  const { data: partners } = useQuery({
    queryKey: ['bk-partners', selKeyword],
    queryFn: () => fetchBkPartnerSummary(selKeyword || ''),
    enabled: showPartners,
  });

  const activeKw = selKeyword || null;

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#f1f5f9', display: 'flex', alignItems: 'center', gap: '0.4rem', margin: 0 }}>
          <Database size={15} color="#a78bfa" /> 수집 기사 탐색
        </h2>
        {keywords.length > 0 && (
          <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap' }}>
            <button onClick={() => { setSelKeyword(''); setOpenYear(null); }}
              style={{ padding: '2px 10px', borderRadius: '12px', border: '1px solid #334155', fontSize: '0.72rem', background: !selKeyword ? '#1d4ed8' : '#0f172a', color: !selKeyword ? '#fff' : '#94a3b8', cursor: 'pointer' }}>
              전체
            </button>
            {keywords.map(k => (
              <button key={k.keyword} onClick={() => { setSelKeyword(k.keyword); setOpenYear(null); }}
                style={{ padding: '2px 10px', borderRadius: '12px', border: '1px solid #334155', fontSize: '0.72rem', background: selKeyword === k.keyword ? '#1d4ed8' : '#0f172a', color: selKeyword === k.keyword ? '#fff' : '#94a3b8', cursor: 'pointer' }}>
                {k.keyword} ({k.count?.toLocaleString()})
              </button>
            ))}
          </div>
        )}
        {keywords.length > 0 && (
          <button onClick={() => setShowPartners(!showPartners)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', marginLeft: 'auto', padding: '3px 10px', borderRadius: '6px', border: '1px solid #334155', fontSize: '0.72rem', background: showPartners ? '#1d4ed8' : '#0f172a', color: '#fff', cursor: 'pointer' }}>
            제휴 현황
          </button>
        )}
        {keywords.length > 0 && (
          <a href={bkExportUrl({ keyword: selKeyword || '' })} download
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '3px 10px', borderRadius: '6px', border: '1px solid #334155', fontSize: '0.72rem', background: '#065f46', color: '#fff', textDecoration: 'none', cursor: 'pointer' }}>
            <Download size={11} /> 엑셀 다운로드{selKeyword ? ` (${selKeyword})` : ' (전체)'}
          </a>
        )}
      </div>

      {showPartners && partners && (
        <div style={{ marginBottom: '0.75rem', border: '1px solid #1e293b', borderRadius: '8px', padding: '0.6rem', maxHeight: '240px', overflowY: 'auto' }}>
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: '0.4rem' }}>
            네이버 제휴 유형 추정 (수집 기사 링크 기준) — CP {partners.data.filter(p => p.inferred === 'cp').length}개 · 검색제휴 {partners.data.filter(p => p.inferred === 'search').length}개
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
            {partners.data.map(p => (
              <span key={p.source} title={`${p.source}: ${p.total}건`}
                style={{ fontSize: '0.68rem', padding: '2px 8px', borderRadius: '10px', border: '1px solid #334155',
                  background: p.inferred === 'cp' ? '#164e63' : '#1e293b',
                  color: p.inferred === 'cp' ? '#67e8f9' : '#94a3b8' }}>
                {p.source} {p.inferred === 'cp' ? '·CP' : ''}
              </span>
            ))}
          </div>
        </div>
      )}

      {years.length === 0 ? (
        <div style={{ color: '#475569', fontSize: '0.85rem' }}>수집된 기사가 없습니다. 위에서 수집을 먼저 실행하세요.</div>
      ) : (
        <div>
          {years.map(y => (
            <div key={y.year}>
              <div onClick={() => setOpenYear(openYear === y.year ? null : y.year)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '5px 0', cursor: 'pointer', color: openYear === y.year ? '#38bdf8' : '#cbd5e1', fontWeight: 600, fontSize: '0.9rem', borderBottom: '1px solid #1e293b' }}>
                {openYear === y.year ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {y.year}년 <span style={{ fontWeight: 400, color: '#64748b', fontSize: '0.75rem' }}>{y.count?.toLocaleString()}건</span>
              </div>
              {openYear === y.year && <TreeNode keyword={activeKw} year={y.year} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BigKindsPage() {
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ['bk-status'], queryFn: fetchBkStatus });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['bk-keywords'] });
    qc.invalidateQueries({ queryKey: ['bk-years'] });
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">
          <Database size={20} color="#a78bfa" /> 빅카인즈 아카이브
        </h1>
      </div>


      <ImportForm onDone={invalidate} />
      <CollectForm onDone={invalidate} />
      <BrowsePanel />

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
